import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Twitter, Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { trackCompetition } from "@/lib/competitionAnalytics";

/**
 * Share competition events / Battles with factual copy only.
 * Does not invent ranks — uses share payload + canonical path.
 */
export default function CompetitionShareModal({
  open,
  onClose,
  headline,
  shareText,
  canonicalPath,
  shareImageUrl,
  battleSlug,
}) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const path = canonicalPath || "/battles";
  const shareUrl = new URL(path.startsWith("http") ? path : path, window.location.origin).toString();
  const text = shareText || headline || "Follow this competition on BackOnFlip.";
  const xShareUrl = (() => {
    const url = new URL("https://x.com/intent/post");
    url.searchParams.set("text", text);
    url.searchParams.set("url", shareUrl);
    return url.toString();
  })();

  const onCopy = async () => {
    const payload = `${text} ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      const input = document.createElement("textarea");
      input.value = payload;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    setCopied(true);
    trackCompetition("SHARE_INTENT", { battle_slug: battleSlug, path });
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md gap-0 overflow-hidden border-white/10 bg-[#0a0a0a] p-0"
        data-testid="competition-share-modal"
      >
        <DialogTitle className="sr-only">Share competition</DialogTitle>
        <DialogDescription className="sr-only">Copy or share this competition update.</DialogDescription>

        <div className="border-b border-white/10 bg-gradient-to-b from-[#0f0f0f] to-black px-6 pb-6 pt-8">
          <div className="mb-4 font-display text-lg font-black tracking-tighter text-gold">BACKONFLIP</div>
          {shareImageUrl ? (
            <img src={shareImageUrl} alt="" className="mb-4 w-full rounded-lg border border-white/10" />
          ) : null}
          <p className="font-display text-xl font-black leading-snug text-white" data-testid="competition-share-headline">
            {headline || "Competition update"}
          </p>
          <p className="mt-3 text-sm text-zinc-400">{text}</p>
          <p className="mt-4 break-all font-mono text-[11px] text-zinc-600">{shareUrl}</p>
        </div>

        <div className="flex gap-2 p-4">
          <a
            href={xShareUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="competition-share-x"
            onClick={() => trackCompetition("SHARE_INTENT", { battle_slug: battleSlug, channel: "x" })}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-black"
          >
            <Twitter className="h-4 w-4" /> Share on X
          </a>
          <button
            type="button"
            data-testid="competition-share-copy"
            onClick={onCopy}
            className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-sm font-bold text-white"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
            Copy
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
